<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumb" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-sm-6">
                <h4 class="card-title">Listas Customizadas</h4>
                <p class="card-title-desc">Gerencie listas de dados para autocomplete nos formulários.</p>
              </div>
              <div class="col-sm-6">
                <div class="float-sm-end d-flex">
                  <div class="text-sm-right">
                    <button type="button" class="btn btn-success btn-rounded mb-2 mr-2" @click="openModal()">
                      <i class="mdi mdi-plus mr-1"></i> Nova Lista
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="table-responsive">
              <table class="table table-centered table-nowrap table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>Slug</th>
                    <th>Colunas</th>
                    <th>Qtd. Items</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in lists" :key="item.id">
                    <td>{{ item.id }}</td>
                    <td>{{ item.name }}</td>
                    <td><code>{{ item.slug }}</code></td>
                    <td>{{ item.columns ? item.columns.length : 0 }}</td>
                    <td>{{ item.items_count || 0 }}</td>
                    <td>
                      <b-dropdown class="card-drop" variant="white" right toggle-class="p-0">
                        <template v-slot:button-content>
                          <i class="mdi mdi-dots-horizontal font-size-18"></i>
                        </template>
                        <b-dropdown-item @click="$router.push(`/custom-lists/${item.id}`)">
                          <i class="fas fa-eye text-primary mr-1"></i> Ver Items
                        </b-dropdown-item>
                        <b-dropdown-item @click="openModal(item)">
                          <i class="fas fa-pencil-alt text-success mr-1"></i> Editar
                        </b-dropdown-item>
                        <b-dropdown-item @click="deleteList(item.id)">
                          <i class="fas fa-trash-alt text-danger mr-1"></i> Excluir
                        </b-dropdown-item>
                      </b-dropdown>
                    </td>
                  </tr>
                  <tr v-if="!lists.length">
                    <td colspan="6" class="text-center text-muted py-4">
                      Nenhuma lista cadastrada.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Criar/Editar Lista -->
    <b-modal v-model="modal" :title="modalTitle" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true">
      <div class="form-group">
        <label for="listName">Nome da Lista</label>
        <input
          id="listName"
          v-model="form.name"
          type="text"
          class="form-control"
          placeholder="Ex: Escolas, Empresas, Hospitais"
          :class="{ 'is-invalid': submitted && !form.name }"
        />
        <div v-if="submitted && !form.name" class="invalid-feedback">
          O campo nome é obrigatório.
        </div>
      </div>

      <div class="form-group mt-4">
        <label>Colunas extras <small class="text-muted">(além do nome principal)</small></label>
        <div v-for="(col, index) in form.columns" :key="index" class="d-flex align-items-center mb-2">
          <input
            v-model="col.key"
            type="text"
            class="form-control form-control-sm mr-2"
            placeholder="Chave (ex: uf)"
            style="flex: 1"
          />
          <input
            v-model="col.label"
            type="text"
            class="form-control form-control-sm mr-2"
            placeholder="Label (ex: Estado)"
            style="flex: 1"
          />
          <div class="custom-control custom-checkbox mr-2">
            <input
              :id="'filterable-' + index"
              type="checkbox"
              class="custom-control-input"
              v-model="col.filterable"
            />
            <label :for="'filterable-' + index" class="custom-control-label">Filtrável</label>
          </div>
          <button class="btn btn-sm btn-danger" @click="removeColumn(index)">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <button type="button" class="btn btn-sm btn-outline-primary mt-1" @click="addColumn()">
          <i class="mdi mdi-plus mr-1"></i> Adicionar Coluna
        </button>
      </div>

      <template v-slot:modal-footer>
        <b-button variant="secondary" @click="closeModal()">Cancelar</b-button>
        <button type="button" class="btn btn-primary" @click="saveList()">Salvar</button>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapGetters } from 'vuex'
import permissionMixin from '@/mixins/permissionMixin'

export default {
  mixins: [permissionMixin],
  components: {
    Layout,
    PageHeader
  },
  name: 'CustomLists',
  page: {
    title: 'Listas Customizadas',
    meta: [{
      name: 'description',
      content: appConfig.description
    }]
  },
  data () {
    return {
      loading: true,
      modal: false,
      modalTitle: 'Nova Lista',
      submitted: false,
      form: {
        name: '',
        columns: []
      },
      title: 'Listas Customizadas',
      breadcrumb: [
        { text: 'Configurações' },
        { text: 'Listas Customizadas', active: true }
      ]
    }
  },
  mounted () {
    this.updateList()
  },
  computed: {
    ...mapGetters('customList', { lists: 'CUSTOM_LISTS' })
  },
  methods: {
    ...mapActions('customList', {
      getAll: 'getAll',
      saveAction: 'save',
      destroyAction: 'destroy'
    }),
    openModal (item = null) {
      this.submitted = false
      if (item) {
        this.modalTitle = 'Editar Lista'
        this.form = {
          id: item.id,
          name: item.name,
          columns: JSON.parse(JSON.stringify(item.columns || []))
        }
      } else {
        this.modalTitle = 'Nova Lista'
        this.form = { name: '', columns: [] }
      }
      this.modal = true
    },
    closeModal () {
      this.modal = false
      this.form = { name: '', columns: [] }
      this.submitted = false
    },
    addColumn () {
      this.form.columns.push({ key: '', label: '', filterable: false })
    },
    removeColumn (index) {
      this.form.columns.splice(index, 1)
    },
    async saveList () {
      this.submitted = true
      if (!this.form.name) return

      try {
        await this.saveAction(this.form)
        successMessage('Sucesso: Lista salva com sucesso.', 2500)
        this.closeModal()
        this.updateList()
      } catch (err) {
        errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
      }
    },
    deleteList (id) {
      Swal.fire({
        title: 'Você tem certeza ?',
        text: 'Todos os items desta lista serão excluídos.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, DESEJO EXCLUIR!'
      }).then(result => {
        if (result.value) {
          try {
            this.destroyAction(id)
            successMessage('Sucesso: Lista excluída com sucesso.', 2500)
            this.updateList()
          } catch (err) {
            errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
          }
        }
      })
    },
    updateList () {
      let loader = this.$loading.show()
      this.getAll().then(() => {
        loader.hide()
      }).catch(error => {
        console.log(error)
        loader.hide()
      })
    }
  }
}
</script>
<style scoped>
.float-sm-end { float: right }
.table-responsive { min-height: 300px; }
.mr-2 { margin-right: 0.5rem; }
</style>
